/**
 * Deep links into native + web maps apps with the whole route as waypoints
 * so a driver hits "open" once and follows turn-by-turn through every stop.
 *
 * Why a single URL (not chunked):
 *   - Google Maps' *path-based* directions URL — `dir/A/B/C/.../Z` — is the
 *     format Google's own web UI emits when you build a multi-stop route by
 *     hand. It handles many more stops than the documented 9-waypoint cap on
 *     `dir/?api=1`. We use it here so a 20- or 25-stop route opens in one tap
 *     instead of forcing the driver to bounce between "Stops 1-9 / 10-18 / …".
 *   - Apple Maps' web preview (maps.apple.com) **does not parse multi-stop
 *     URLs at all** in practice — the documented `daddr=A+to:B+to:C` chain
 *     just renders the first stop. The `maps://` URL *scheme*, on the other
 *     hand, is intercepted directly by the native Apple Maps app on iOS /
 *     macOS, which is the only surface where multi-stop URL parsing actually
 *     fires. So we emit `maps://` and gate the button to Apple-only browsers.
 *     Non-Apple users get the universal "Copy stop addresses" fallback below,
 *     which pastes into any maps app on any platform.
 *
 * Hand-built strings only — URLSearchParams over-encodes `|`, `+to:`, and `,`
 * which the maps apps need raw.
 */

export interface MapsRouteStop {
  lat: number;
  lng: number;
  /** Optional label — used by the Copy-addresses fallback so the clipboard
   *  text reads "John Smith — 123 Main St" rather than just "123 Main St". */
  label?: string;
  /** Optional street address ("6534 Buttercup Ln, Lorain, OH 44053"). Used
   *  preferentially by Apple Maps because addresses tokenise on `+to:` more
   *  cleanly than comma-bearing lat/lng pairs. Google's path-based URL
   *  always uses lat/lng — it doesn't have this problem. */
  address?: string;
}

type LatLng = { lat: number; lng: number };

function fmt(p: LatLng): string {
  return `${p.lat},${p.lng}`;
}

function sameSpot(a: LatLng, b: LatLng): boolean {
  return Math.abs(a.lat - b.lat) < 1e-8 && Math.abs(a.lng - b.lng) < 1e-8;
}

// ---------- Google Maps -----------------------------------------------------

/**
 * Single Google Maps URL covering every stop using the path-based directions
 * format (`/maps/dir/A/B/C/.../Z`). For routes whose `destination` is a real
 * different end point (custom end depot), it gets appended as the final
 * segment. For round-trips (destination === origin) the return is implicit —
 * we don't duplicate the depot, which Google's URL parser would reject.
 */
export function buildGoogleMapsRouteUrl(
  origin: LatLng,
  stops: MapsRouteStop[],
  destination: LatLng | null,
): string {
  const segments: string[] = [fmt(origin)];
  for (const s of stops) segments.push(fmt(s));
  if (destination && !sameSpot(origin, destination)) {
    segments.push(fmt(destination));
  }
  return `https://www.google.com/maps/dir/${segments.join("/")}?travelmode=driving`;
}

// ---------- Apple Maps (Apple-platforms only) -------------------------------

function appleStopToken(stop: { lat: number; lng: number; address?: string }): string {
  if (stop.address && stop.address.trim()) {
    // encodeURIComponent gives us %20 for spaces and %2C for commas inside
    // the address. The `+to:` separator stays as literal `+to:` between
    // tokens — Apple's parser needs that literal `+` to split.
    return encodeURIComponent(stop.address.trim());
  }
  return fmt(stop);
}

/**
 * Apple Maps universal URL — `https://maps.apple.com/?...`. This single URL
 * works on every platform the user might be on:
 *
 *   - iOS / iPadOS / macOS Safari: Universal Links route this to the native
 *     Apple Maps app, where the `+to:` chain parses to multi-stop.
 *   - macOS Chrome / Firefox: opens maps.apple.com web preview; clicking
 *     "Open in Maps" inside the web preview hands off to the native app.
 *   - Windows / Linux / Android: opens maps.apple.com web preview. Apple's
 *     web preview has historically rendered only the first stop of a
 *     multi-stop URL — that's an Apple-side gap, not a URL-encoding issue,
 *     and there's no client-side trick that changes their parser. Users on
 *     these platforms have the universal "Copy addresses" fallback (also
 *     rendered in the UI) so they can paste the chain into any app.
 *
 * Format details:
 *   - `+to:` between tokens in the raw URL string. In URL form-encoding `+`
 *     decodes to a space, so Apple Maps receives `" to:"` as the separator,
 *     which matches Apple's multi-stop parser. Using `%2Bto:` (literal `+`)
 *     was a bug — Apple Maps splits on space-to-colon, not plus-to-colon.
 *   - Addresses preferred over lat/lng because comma-bearing coords confuse
 *     Apple's splitter (the comma between lat,lng collides with the comma
 *     inside the address). Encoded via encodeURIComponent → %20 for spaces,
 *     %2C for commas inside the address. The `+to:` separator stays clean.
 */
export function buildAppleMapsRouteUrl(
  origin: LatLng,
  stops: MapsRouteStop[],
  destination: LatLng | null,
): string {
  const tokens: string[] = [];
  for (const s of stops) tokens.push(appleStopToken(s));
  if (destination && !sameSpot(origin, destination)) {
    tokens.push(appleStopToken({ lat: destination.lat, lng: destination.lng }));
  }

  if (tokens.length === 0) {
    return `https://maps.apple.com/?ll=${fmt(origin)}&dirflg=d`;
  }
  // +to: in the raw URL string: `+` form-decodes to a space, so Apple Maps
  // receives "addr1 to:addr2 to:addr3" which its parser splits on " to:".
  const daddr = tokens.join("+to:");
  return `https://maps.apple.com/?saddr=${fmt(origin)}&daddr=${daddr}&dirflg=d`;
}

// ---------- Universal clipboard fallback ------------------------------------

/**
 * Returns a newline-separated address list that pastes cleanly into any maps
 * app (Apple, Google, Waze, HERE, etc.) on any platform. This is the
 * always-works escape hatch when a deep-link can't be honored (e.g. Windows
 * desktop user where Apple Maps multi-stop is impossible).
 *
 * Format:
 *   Start: <origin label or coords>
 *   1. <stop label> — <address>
 *   2. <stop label> — <address>
 *   …
 *   End: <destination label or coords>     ← only when destination differs from origin
 *
 * The driver pastes this into any nav app's "search" field or note-taking
 * app, and can re-enter each stop one by one. Not as clean as a deep link
 * but it always works.
 */
export function buildRouteAddressList(
  origin: LatLng,
  stops: MapsRouteStop[],
  destination: LatLng | null,
  originLabel = "Start",
  destinationLabel = "End",
): string {
  const lines: string[] = [];
  lines.push(`${originLabel}: ${fmt(origin)}`);
  stops.forEach((s, i) => {
    const num = i + 1;
    const addr = s.address?.trim() || fmt(s);
    const label = s.label?.trim();
    lines.push(label ? `${num}. ${label} — ${addr}` : `${num}. ${addr}`);
  });
  if (destination && !sameSpot(origin, destination)) {
    lines.push(`${destinationLabel}: ${fmt(destination)}`);
  }
  return lines.join("\n");
}

// ---------- Platform detection ----------------------------------------------

/** True when the browser is iOS / iPadOS / macOS — the only places the
 *  `maps://` URL scheme reliably opens the native Apple Maps app. */
export function preferAppleMaps(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod|Macintosh/.test(ua);
}
